import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth'

const routes = [
  {
    path: '/',
    redirect: '/devices'
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/auth/LoginView.vue'),
    meta: { requiresAuth: false, title: 'auth.login' }
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('../views/auth/RegisterView.vue'),
    meta: { requiresAuth: false, title: 'auth.register' }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('../views/dashboard/DashboardView.vue'),
    meta: { requiresAuth: true, title: 'dashboard.title' }
  },
  {
    path: '/devices',
    name: 'devices',
    component: () => import('../views/devices/DeviceListView.vue'),
    meta: { requiresAuth: true, title: 'devices.title' }
  },
  {
    path: '/devices/create',
    name: 'device-create',
    component: () => import('../views/devices/DeviceFormView.vue'),
    meta: { requiresAuth: true, title: 'devices.createNew' }
  },
  {
    path: '/devices/:id/edit',
    name: 'device-edit',
    component: () => import('../views/devices/DeviceFormView.vue'),
    meta: { requiresAuth: true, title: 'devices.editDevice' }
  },
  {
    path: '/devices/:id',
    name: 'device-detail',
    component: () => import('../views/devices/DeviceDetailView.vue'),
    meta: { requiresAuth: true, title: 'devices.deviceInformation' },
    beforeEnter: (to, from, next) => {
      console.log('Router beforeEnter for devices route')
      // Just continue normal navigation - component will handle data loading
      next()
    }
  },
  {
    path: '/devices/:deviceId/components',
    name: 'device-components',
    component: () => import('../views/devices/components/ComponentListView.vue'),
    meta: { requiresAuth: true, title: 'components.title' }
  },
  {
    path: '/devices/:deviceId/components/create',
    name: 'component-create',
    component: () => import('../views/devices/components/ComponentFormView.vue'),
    meta: { requiresAuth: true, title: 'components.addComponent' }
  },
  {
    path: '/devices/:deviceId/components/:componentId/schedules',
    name: 'ComponentSchedules',
    component: () => import('../views/devices/components/ComponentSchedulesView.vue'),
    meta: { requiresAuth: true, title: 'components.schedules.title' }
  },
  {
    path: '/devices/:deviceId/components/:componentId',
    name: 'component-detail',
    component: () => import('../views/devices/components/ComponentDetailView.vue'),
    meta: { requiresAuth: true, title: 'components.details' }
  },
  {
    path: '/devices/:deviceId/components/:componentId/edit',
    name: 'component-edit',
    component: () => import('../views/devices/components/ComponentFormView.vue'),
    meta: { requiresAuth: true, title: 'common.edit' }
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('../views/profile/ProfileView.vue'),
    meta: { requiresAuth: true, title: 'profile.title' }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('../views/NotFoundView.vue'),
    meta: { title: 'common.error' }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    // Always scroll to top
    return { top: 0 }
  },
})

// Navigation guard
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  // Enhanced logging for navigation debugging
  console.log('Navigation debug:')
  console.log(`- Route: ${from.path} → ${to.path}`)
  console.log(`- Auth required: ${requiresAuth}`)
  console.log(`- Auth state: token=${!!authStore.token}, user=${!!authStore.user}`)
  console.log(`- localStorage token: ${!!localStorage.getItem('auth_token')}`)

  // Refresh token from localStorage if not in store
  if (!authStore.token && localStorage.getItem('auth_token')) {
    console.log('Token found in localStorage but not in store - refreshing')
    authStore.setToken(localStorage.getItem('auth_token'))
  }

  // Re-check token status after potential refresh
  const hasToken = !!authStore.token

  // Check token validity if it exists
  let isValidToken = false
  if (hasToken) {
    isValidToken = authStore.checkTokenExpiration()
    console.log('Token validation result:', isValidToken)
  }

  if (requiresAuth && !hasToken) {
    // Redirect to login if authentication is required but user is not authenticated
    console.log('Auth required but no token - redirecting to login')
    next({ name: 'login', query: { redirect: to.fullPath } })
  } else if ((to.name === 'login' || to.name === 'register') && hasToken) {
    // Redirect to dashboard if user is already authenticated and tries to access login/register
    console.log('User is authenticated but trying to access login/register - redirecting to dashboard')
    next({ name: 'dashboard' })
  } else {
    // Proceed as normal
    console.log('Proceeding with navigation normally')
    next()
  }
})

// We're using the built-in navigation guard above, no need for the external one
// import { setupAuthGuards } from '../plugins/auth-guard'
// setupAuthGuards(router)

// Add i18n for title translations
import i18n from '../i18n'

// Set page title based on route meta
router.afterEach((to) => {
  const { t } = i18n.global

  // Set default title
  let title = t('common.app_name')

  // Get title from route meta if available
  if (to.meta.title) {
    title = t(to.meta.title) + ' | ' + t('common.app_name')
  }

  // Set the page title
  document.title = title
})

export default router